<template>
  <div class="page">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
      <h2 class="page-title" style="margin:0;">{{ $t('menu.tickets') }}</h2>
      <el-button type="primary" class="btn-glass" @click="openCreate">{{ $t('common.new') }}</el-button>
    </div>

    <div class="glass-card" style="margin-top:14px; padding:14px; overflow-x:auto;">
      <el-table :data="itemsPage" style="width:100%;" @row-click="open">
        <el-table-column prop="caseNo" :label="$t('field.caseNo')" width="140" />
        <el-table-column prop="title" :label="$t('field.title')" min-width="220" />
        <el-table-column prop="status" :label="$t('field.status')" width="170">
          <template slot-scope="s">
            <span class="status-pill-wrap">
              <span :class="['tag-pill', isSubmittedNotice(s.row) || s.row.hasReminder ? 'tag-pill--with-dot' : '']">{{ caseStatusLabel(s.row) }}</span>
              <span v-if="isSubmittedNotice(s.row) || s.row.hasReminder" class="status-pill-dot" />
            </span>
          </template>
        </el-table-column>
        <el-table-column :label="flowColumnLabel" width="220">
          <template slot-scope="s">
            <span class="tag-pill">{{ flowLabel(s.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdAtLabel" :label="$t('field.createdAt')" width="190" />
      </el-table>
      <div style="display:flex;justify-content:flex-end;margin-top:12px;">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="items.length"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[5,10,20,50]"
          @current-change="(p) => (page = p)"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog
      :title="$t('ticket.newTicket')"
      :visible.sync="createDialog"
      width="760px"
      top="6vh"
      :append-to-body="true"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @close="onCreateClose"
    >
      <el-form :model="form" label-position="top">
        <el-form-item :label="$t('field.title')">
          <el-input v-model="form.title" maxlength="200" />
        </el-form-item>
        <el-form-item :label="$t('field.message')">
          <el-input v-model="form.content" type="textarea" :rows="6" />
        </el-form-item>
        <el-form-item :label="$t('field.attachments')">
          <FileUploader ref="uploader" v-model="files" @uploading-change="uploading = $event" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createDialog=false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          class="btn-glass"
          :loading="loading || uploading"
          :disabled="loading || uploading"
          @click="submit"
        >
          {{ $t('common.submit') }}
        </el-button>
      </span>
    </el-dialog>

    <SecondaryCaseDialog
      v-model="detailDialog"
      :case-no="currentCaseNo"
      mode="submitted"
      @reminder-read="onReminderRead"
      @changed="load"
    />
  </div>
</template>

<script>
import { secondarySubmitCase, secondarySubmittedCases } from '../../api/secondary';
import FileUploader from '../../components/FileUploader.vue';
import SecondaryCaseDialog from '../../components/SecondaryCaseDialog.vue';
import { caseStatusLabel as formatCaseStatusLabel, flowLabel as formatFlowLabel, localText as formatLocalText } from '../../utils/caseDisplay';

export default {
  name: 'SecondarySubmit',
  components: { FileUploader, SecondaryCaseDialog },
  data() {
    return {
      loading: false,
      uploading: false,
      createDialog: false,
      detailDialog: false,
      currentCaseNo: '',
      form: { title: '', content: '' },
      files: [],
      items: [],
      page: 1,
      pageSize: 10
    };
  },
  computed: {
    version() {
      return this.$store.state.versions.secondaryCases;
    },
    fileIds() {
      return (this.files || []).map((x) => (x ? x.id : null)).filter((x) => x != null);
    },
    itemsPage() {
      const start = (this.page - 1) * this.pageSize;
      return (this.items || []).slice(start, start + this.pageSize);
    },
    flowColumnLabel() {
      return formatLocalText(this, '流转', 'Flow');
    }
  },
  watch: {
    version() {
      this.load();
    },
    detailDialog(v) {
      if (v) return;
      if (this.$route.query && this.$route.query.caseNo) {
        const q = { ...this.$route.query };
        delete q.caseNo;
        this.$router.replace({ path: this.$route.path, query: q }).catch(() => {});
      }
    },
    '$route.query.caseNo'() {
      this.maybeOpenFromQuery();
    }
  },
  created() {
    this.load();
    this.maybeOpenFromQuery();
  },
  methods: {
    maybeOpenFromQuery() {
      const caseNo = this.$route.query && this.$route.query.caseNo ? String(this.$route.query.caseNo) : '';
      if (!caseNo) return;
      this.currentCaseNo = caseNo;
      this.detailDialog = true;
    },
    async load() {
      const r = await secondarySubmittedCases();
      this.items = (r || []).map((x) => ({ ...x, createdAtLabel: x.createdAt ? new Date(x.createdAt).toLocaleString() : '-' }));
      const maxPage = Math.max(1, Math.ceil(this.items.length / this.pageSize));
      this.page = Math.min(this.page, maxPage);
      this.$store.dispatch('refreshSecondaryBadges');
    },
    onSizeChange(s) {
      this.pageSize = s;
      this.page = 1;
    },
    isSubmittedNotice(row) {
      return !!(row && row.hasNewNotice);
    },
    caseStatusLabel(row) {
      return formatCaseStatusLabel(this, row);
    },
    flowLabel(row) {
      return formatFlowLabel(this, row);
    },
    openCreate() {
      this.createDialog = true;
    },
    open(row) {
      if (!row || !row.caseNo) return;
      this.currentCaseNo = row.caseNo;
      this.detailDialog = true;
    },
    onReminderRead(caseNo) {
      if (!caseNo) return;
      this.items = (this.items || []).map((x) => String(x.caseNo) === String(caseNo) ? { ...x, hasReminder: false, hasNewNotice: false } : x);
      this.$store.dispatch('refreshSecondaryBadges');
    },
    onCreateClose() {
      try {
        if (this.$refs.uploader && this.$refs.uploader.cancelUploads) this.$refs.uploader.cancelUploads();
      } catch (e) {}
      this.form = { title: '', content: '' };
      this.files = [];
      this.uploading = false;
    },
    async submit() {
      if (!this.form.title) return this.$message.error(this.$t('ticket.requiredTitle'));
      if (!this.form.content) return this.$message.error(this.$t('ticket.requiredContent'));
      this.loading = true;
      try {
        await secondarySubmitCase({ title: this.form.title, content: this.form.content, fileIds: this.fileIds });
        try {
          if (this.$refs.uploader && this.$refs.uploader.markCommitted) this.$refs.uploader.markCommitted(this.fileIds);
        } catch (e) {}
        this.$message.success(this.$t('ticket.submittedToPool'));
        this.createDialog = false;
        this.onCreateClose();
        this.$store.commit('bumpVersion', 'secondaryCases');
        await this.load();
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.status-pill-wrap {
  position: relative;
  display: inline-block;
}
.tag-pill--with-dot {
  padding-right: 18px;
}
.status-pill-dot {
  position: absolute;
  top: 3px;
  right: 5px;
  width: 7px;
  height: 7px;
  border-radius: 99px;
  background: rgba(239, 68, 68, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.95);
}
</style>
